import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { customElement, property } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { CSEPaginationButton } from '@cloudsuite/mosaic-elements'

export class PaginationOnNavigateEvent extends Event {
  static type = 'pagination-on-navigate' as const
  pageLink: string

  constructor(pageLink: string) {
    super(PaginationOnNavigateEvent.type, { bubbles: true, composed: true })
    this.pageLink = pageLink
  }
}

@customElement('csc-pagination')
export class CSCPagination extends LitElement {
  static override styles: CSSResultGroup = [hostStyle]

  @property({ type: Number, attribute: 'current-page' })
  currentPage: number = 1

  @property({ type: Number, attribute: 'number-of-pages' })
  numberOfPages: number = 2

  @property({ type: String, attribute: 'first-link' })
  firstLink: string = ''

  @property({ type: String, attribute: 'prev-link' })
  prevLink: string = ''

  @property({ type: String, attribute: 'next-link' })
  nextLink: string = ''

  @property({ type: String, attribute: 'last-link' })
  lastLink: string = ''


  private gotoPage(pageNumber: number, pageLink: string, event: Event) {
    const target = event.target as CSEPaginationButton
    if (target.disabled) {
      return
    }

    event.preventDefault()
    this.dispatchEvent(new PaginationOnNavigateEvent(pageLink))
    this.currentPage = pageNumber
  }

  override render() {
    return this.numberOfPages > 0 ? html`
      <cse-group row-gap="05" justify="center">
        <cse-pagination-button
          previous
          ?disabled="${this.currentPage === 1}"
          url="${this.prevLink}"
          @click="${(event: Event) => this.gotoPage(this.currentPage - 1, this.prevLink, event)}"
        >
          ${t('previous_pagination_button', {
            ns: 'csc-pagination',
            default: 'Previous'
          })}
        </cse-pagination-button>

        ${ this.numberOfPages > 1
          ? html`
            <cse-pagination-button
              first
              ?active="${this.currentPage === 1}"
              url="${this.firstLink}"
              @click="${(event: Event) => this.gotoPage(1, this.firstLink, event)}"
            >1</cse-pagination-button>`
          : nothing
        }

        ${ this.numberOfPages > 3 &&
          this.currentPage !== 1 &&
          this.currentPage !== 2 &&
          this.currentPage !== 3
          ? html`<cse-pagination-button disabled>...</cse-pagination-button>`
          : nothing
        }

        ${this.currentPage !== 1 &&
          this.currentPage !== 2
          ? html `
            <cse-pagination-button
              previous-number
              url="${this.prevLink}"
              @click="${(event: Event) => this.gotoPage(this.currentPage - 1, this.prevLink, event)}"
            >${this.currentPage - 1}</cse-pagination-button>`
          : nothing
        }

        ${ this.numberOfPages === 1 || (
          this.numberOfPages > 2 &&
          this.currentPage !== 1 &&
          this.currentPage !== this.numberOfPages)
          ? html`<cse-pagination-button active>${this.currentPage}</cse-pagination-button>`
          : nothing
        }

        ${this.currentPage !== this.numberOfPages &&
          this.currentPage !== this.numberOfPages - 1
          ? html `
            <cse-pagination-button
              next-number
              url="${this.nextLink}"
              @click="${(event: Event) => this.gotoPage(this.currentPage + 1, this.nextLink, event)}"
            >${this.currentPage + 1}</cse-pagination-button>`
          : nothing
        }

        ${ this.numberOfPages > 3 &&
          this.currentPage != this.numberOfPages &&
          this.currentPage != this.numberOfPages - 1 &&
          this.currentPage != this.numberOfPages - 2
          ? html`<cse-pagination-button disabled>...</cse-pagination-button>`
          : nothing
        }

        ${ this.numberOfPages > 1
          ? html`
          <cse-pagination-button
            last
            ?active="${this.currentPage === this.numberOfPages}"
            url="${this.lastLink}"
            @click="${(event: Event) => this.gotoPage(this.numberOfPages, this.lastLink, event)}"
          >${this.numberOfPages}</cse-pagination-button>
          `
          :nothing
        }

        <cse-pagination-button
          next
          ?disabled=${this.currentPage === this.numberOfPages}
          url="${this.nextLink}"
          @click="${(event: Event) => this.gotoPage(this.currentPage + 1, this.nextLink, event)}"
        >
          ${t('next_pagination_button', {
            ns: 'csc-pagination',
            default: 'Next'
          })}
        </cse-pagination-button>
      </cse-group>
    ` : nothing
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-pagination': CSCPagination,
  }
}
